import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";

interface UseUnsavedChangesProps<T> {
  currentData: T;
  isEditMode: boolean;
  shouldTrackInitialData: boolean; // e.g., when DDL is loaded for new mode
}

export const useUnsavedChanges = <T>({
  currentData,
  isEditMode,
  shouldTrackInitialData,
}: UseUnsavedChangesProps<T>) => {
  const [showModal, setShowModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(
    null,
  );
  // track whether the pending action is a browser "back"
  const [pendingIsBack, setPendingIsBack] = useState(false);

  const initialDataRef = useRef<string | null>(null);
  const isSavingRef = useRef(false);
  const navigate = useNavigate();

  const ignoreNextPopRef = useRef(false);

  // Check if there are unsaved changes (memoized)
  const hasUnsavedChanges = useMemo(() => {
    if (!initialDataRef.current) return false;
    const currentDataString = JSON.stringify(currentData);
    return currentDataString !== initialDataRef.current;
  }, [currentData]);

  // Set initial data when component is ready (new mode only)
  useEffect(() => {
    if (!isEditMode && shouldTrackInitialData && !initialDataRef.current) {
      initialDataRef.current = JSON.stringify(currentData);
    }
  }, [isEditMode, shouldTrackInitialData, currentData]);

  // Update initial data after successful save
  useEffect(() => {
    if (isSavingRef.current) {
      initialDataRef.current = JSON.stringify(currentData);
      isSavingRef.current = false;
    }
  }, [currentData]);

  // Intercept all navigation attempts
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // 1) SidebarHeader back button ONLY
      const backBtn = target.closest(
        'button[data-nav="back-to-portals"]',
      ) as HTMLButtonElement | null;

      if (backBtn) {
        e.preventDefault();
        e.stopPropagation();

        setPendingIsBack(false);
        setPendingNavigation("/portal-configuration");
        setShowModal(true);
        return;
      }

      // 2) Any in app <a> navigation (sidebar links, breadcrumbs, etc.)
      const link = target.closest("a[href]") as HTMLAnchorElement | null;
      if (link?.href) {
        const url = new URL(link.href);
        if (url.origin === window.location.origin) {
          const next = url.pathname + url.search + url.hash;
          const current =
            window.location.pathname +
            window.location.search +
            window.location.hash;

          // optional: avoid opening modal if it's the same URL
          if (next === current) return;

          e.preventDefault();
          e.stopPropagation();

          setPendingIsBack(false);
          setPendingNavigation(next);
          setShowModal(true);
          return;
        }
      }
    };

    document.addEventListener("click", handleClick, true);
    return () => {
      document.removeEventListener("click", handleClick, true);
    };
  }, [hasUnsavedChanges]);

  const guardPushedRef = useRef(false);
  const pageUrlRef = useRef(
    window.location.pathname + window.location.search + window.location.hash,
  );

  useEffect(() => {
    if (!hasUnsavedChanges) {
      guardPushedRef.current = false;
      return;
    }
    if (guardPushedRef.current) return;
    guardPushedRef.current = true;

    pageUrlRef.current =
      window.location.pathname + window.location.search + window.location.hash;

    window.history.pushState(
      { __unsaved_guard__: true },
      "",
      pageUrlRef.current,
    );
  }, [hasUnsavedChanges]);

  // Handle browser back button and page refresh
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!hasUnsavedChanges) return;
      e.preventDefault();
      (e as any).returnValue = "";
    };

    const handlePopState = (e: PopStateEvent) => {
      if (!hasUnsavedChanges) return;

      if (ignoreNextPopRef.current) {
        ignoreNextPopRef.current = false;
        return;
      }

      // keep user on same page until confirmed
      window.history.pushState(
        { __unsaved_guard__: true },
        "",
        pageUrlRef.current,
      );

      setPendingIsBack(true);
      setPendingNavigation(null);
      setShowModal(true);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [hasUnsavedChanges]);

  // Mark that save is in progress
  const markSaving = () => {
    isSavingRef.current = true;
  };

  const handleCancelNavigation = () => {
    setShowModal(false);
    setPendingIsBack(false);
    setPendingNavigation(null);
  };

  // Handle confirmation to navigate away
  const handleConfirmNavigation = () => {
    setShowModal(false);
    initialDataRef.current = null;

    if (pendingIsBack) {
      setPendingIsBack(false);
      // allow the upcoming popstate caused by back() to pass
      ignoreNextPopRef.current = true;
      navigate(-2); // goes to the real previous page, skips guard
      return;
    }

    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  // Manually set initial data (for edit mode after data is fetched)
  const setInitialData = (data: T) => {
    initialDataRef.current = JSON.stringify(data);
  };

  return {
    showModal,
    hasUnsavedChanges,
    setShowModal,
    handleConfirmNavigation,
    handleCancelNavigation,
    markSaving,
    setInitialData,
  };
};
